import Alert from "@storeblocks/alert";
import Button from "@storeblocks/button";
import axios, { AxiosRequestConfig } from "axios";
import React, { useEffect, useState } from "react";
import {
  isProductionEnvironment,
  removeSigningOrder,
} from "../../utils/requestUtils";
import { AlertContent, Wrapper } from "./styled";

/* Keep track of whether we are running in production.
 * We always default to true as a failsafe, to ensure we never
 * display any server error messages to real customers.
 */
let isProduction = true;

const getErrorDescription = (errorMessage: string) => {
  if (!isProduction) {
    return `Serveren sier: ${
      errorMessage ? errorMessage : "Ingen feilmelding"
    }`;
  } else {
    return "Prøv igjen, eller forsøk å laste inn siden på nytt om problemet fortsetter.";
  }
};

const getErrorTitle = (status: number) => {
  if (!isProduction) {
    switch (status) {
      case 500: {
        return `Ukjent feil fra server. (${status})`;
      }
      case 400: {
        return `Feil i kommunikasjon med server.  (${status})`;
      }
      case 401: {
        return `Autentiseringsfeil. Logg inn på nytt. (${status})`;
      }
      case 404: {
        return `Fant ikke ressursen på server. (${status})`;
      }
      default: {
        return `Ukjent feil. (${status})`;
      }
    }
  } else {
    return "Oisann, her gikk noe galt!";
  }
};

/** GlobalErrorHandler
 *
 *  Generalized error handler.
 *  Displays an error message in lower right corner whenever anything fails.
 *  Also logs to console in development for debugging.
 */
export const GlobalErrorHandler = () => {
  const [errorTitle, setErrorTitle] = useState<string | undefined>();
  const [errorDescription, setErrorDescription] = useState<
    string | undefined
  >();

  const logErrorMessage = (error: any) => {
    console.error(error);
  };

  useEffect(() => {
    isProductionEnvironment().then((res: boolean) => {
      isProduction = res;
    });

    const requestInterceptor = axios.interceptors.request.use(
      async (config) => {
        const req: AxiosRequestConfig = { ...config };
        setErrorTitle(undefined);
        setErrorDescription(undefined);
        return req;
      },
    );

    const responseInterceptor = axios.interceptors.response.use(
      async (res) => {
        return res;
      },
      async (error) => {
        const { errorMessage } = error.response.data;
        const description = getErrorDescription(errorMessage);
        setErrorDescription(description);

        const { status } = error.response;
        const title = getErrorTitle(status);
        setErrorTitle(title);

        /*
         * If we get a 409 error with the message SIGNING_ORDER_EXISTS we will
         * attempt to remove the existing signing order based on the request returned
         * the case id will be in the url
         */
        if (status === 409 && errorMessage === "SIGNING_ORDER_EXISTS") {
          const { request } = error.response;
          if (request) {
            removeSigningOrder(request);
          }
        }

        if (!isProduction) {
          logErrorMessage(error);
        }
      },
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, []);

  return (
    <Wrapper>
      {errorTitle && (
        <Alert title={errorTitle} variant="error">
          <AlertContent>
            <p>{errorDescription}</p>
            <Button onClick={() => setErrorTitle(undefined)}> OK </Button>
          </AlertContent>
        </Alert>
      )}
    </Wrapper>
  );
};

export default GlobalErrorHandler;
